import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabaseClient";
import { useUser } from "../contexts/UserContext";

interface PollVoteBoxProps {
  postId: number;
  poll: {
    question: string;
    options: string[];
  };
  readonly?: boolean;   // ✅ 종료 시 결과만 보여주는 모드
}

interface VoteCount {
  [index: number]: number;
}

export default function PollVoteBox({ postId, poll, readonly = false }: PollVoteBoxProps) {
  const { user } = useUser();
  const [selected, setSelected] = useState<number | null>(null);
  const [results, setResults] = useState<VoteCount>({});
  const [voted, setVoted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function fetchVoteStatus() {
    if (!user) return;
    const { data: existing } = await supabase
      .from("votes")
      .select("*")
      .eq("post_id", postId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (existing) {
      setSelected(existing.selected_index);
      setVoted(true);
    } else {
      setSelected(null);
      setVoted(false);
    }
  }

  async function fetchResults() {
    const { data: allVotes } = await supabase
      .from("votes")
      .select("post_id, selected_index")
      .eq("post_id", postId);

    const counts: VoteCount = {};
    poll.options.forEach((_, i) => (counts[i] = 0));
    allVotes?.forEach((vote) => {
      counts[vote.selected_index]++;
    });
    setResults(counts);
  }

  // ✅ 투표/취소 처리
  async function handleVote(index: number) {
    if (!user || readonly) return;

    if (selected === index) {
      const confirmDelete = window.confirm("이미 투표한 항목입니다.\n투표를 취소하시겠습니까?");
      if (!confirmDelete) return;

      setLoading(true);
      const { error } = await supabase
        .from("votes")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id);

      if (!error) {
        setSelected(null);
        setVoted(false);
        setTimeout(() => {
          fetchVoteStatus();
          fetchResults();
          setLoading(false);
        }, 300);
      } else {
        setLoading(false);
        alert("투표 취소에 실패했습니다.");
      }
      return;
    }

    const confirmVote = window.confirm("이 항목에 투표하시겠습니까?");
    if (!confirmVote) return;

    setLoading(true);
    const { error } = await supabase.from("votes").insert({
      post_id: postId,
      user_id: user.id,
      selected_index: index,
    });
    setLoading(false);

    if (!error) {
      setSelected(index);
      setVoted(true);
      setTimeout(() => {
        fetchVoteStatus();
        fetchResults();
        setLoading(false);
      }, 300);
    } else {
      setLoading(false);
      alert("투표 실패 또는 이미 참여한 투표입니다.");
    }
  }

  useEffect(() => {
    fetchVoteStatus();
    fetchResults();
    // eslint-disable-next-line
  }, [postId, user?.id]);

  const totalVotes = Object.values(results).reduce((sum, count) => sum + count, 0);

  return (
    <div className="border p-4 rounded bg-gray-50">
      <p className="font-semibold mb-2">🗳️ {poll.question}</p>
      <ul className="space-y-2">
        {poll.options.map((opt, i) => {
          const count = results[i] || 0;
          const percent = totalVotes ? Math.round((count / totalVotes) * 100) : 0;
          const isSelected = selected === i;
          return (
            <li key={i}>
              <button
                disabled={loading || !user || readonly}
                onClick={() => !readonly && handleVote(i)}
                className={`w-full text-left p-2 rounded border ${
                  isSelected ? "bg-blue-100 border-blue-500" : "hover:bg-gray-100"
                }`}
              >
                {opt}
                {/* ✅ 항상 결과 보이도록 */}
                <span className="float-right text-sm text-gray-500">
                  {count}표 ({percent}%)
                </span>
                {loading && isSelected && !readonly && (
                  <span className="float-right text-xs text-blue-500 ml-2">처리중...</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {!user && !readonly && (
        <p className="text-sm text-gray-500 mt-2">※ 로그인 후 투표하실 수 있습니다.</p>
      )}
    </div>
  );
}
